import React, { useEffect, useRef } from 'react';
import { useMessages } from '../contexts/MessageContext';
import { Paper, Typography, Divider, Button, TextField, Box, CircularProgress } from '@material-ui/core';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz/toZonedTime';
import styled from 'styled-components';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const MessageContainer = styled(Paper)`
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ReplyForm = styled.form`
  margin-top: auto;
  padding-top: 20px;
`;

const MessageBubble = styled(Paper)`
  padding: 12px;
  margin: 8px 0;
  max-width: 70%;
  ${props => props.isReply ? `
    align-self: flex-end;
    background-color: #e3f2fd;
  ` : `
    align-self: flex-start;
    background-color: #f5f5f5;
  `}
`;

const MessageDetail = () => {
    const { selectedMessage, sendReply, messageThreads, getTwilioNumber } = useMessages();
    const [replyText, setReplyText] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (!selectedMessage?.conversation?.length) return;

        // Delay until browser has rendered the new list
        requestAnimationFrame(() => {
            if (bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        });
    }, [selectedMessage?.conversation?.length]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedMessage || !replyText.trim()) return;
        setSending(true);
        setError(null);
        try {
            // In a real app, you'd get the from number from user settings or context
            const fromNumber = await getTwilioNumber();
            const result = await sendReply(fromNumber, selectedMessage.from_number, replyText, selectedMessage.id);

            if (result.success) {
                setReplyText('');
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    if (!selectedMessage) {
        return (
            <MessageContainer elevation={3}>
                <Typography variant="h6" color="textSecondary">
                    Select a message to view
                </Typography>
            </MessageContainer>
        );
    }

    return (
        <Box display="flex" flexDirection="column" height="90%">
            {/* Scrollable Message Area */}
            <Box
                flex="1"
                overflow="auto"
                component={MessageContainer}
                elevation={3}
                px={2}
                py={1}
                style={{ height: '65vh', overflowY: 'auto' }}
            >
                {selectedMessage?.conversation.map((message) => (
                    <MessageBubble key={message.id} elevation={1} isReply={message.direction === "outbound"}>
                        <Typography variant="subtitle2" color="textSecondary">
                            {format(toZonedTime(new Date(message.created_at + "Z"), timeZone), 'MMM d, h:mm a')}
                        </Typography>
                        <Typography variant="body1">{message.body}</Typography>
                        <Typography variant="caption" color="textSecondary">
                            {message.direction === "outbound" ? (
                                <>
                                    You replied
                                    {message.status === 'sent' && ' ✔'}
                                    {message.status === 'delivered' && ' ✔✔'}
                                </>
                            ) : (
                                'They said'
                            )}
                        </Typography>
                    </MessageBubble>
                ))}
                <div ref={bottomRef} />
            </Box>

            {/* Fixed Reply Box */}
            <ReplyForm onSubmit={handleSubmit}>
                <TextField
                    label="Reply"
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={sending}
                />
                <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!replyText.trim() || sending}
                    >
                        {sending ? <CircularProgress size={24} /> : 'Send'}
                    </Button>
                </Box>
                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}
            </ReplyForm>
            {/* <Box component="form" onSubmit={handleSubmit} p={2} borderTop="1px solid #eee">
                <ReplyForm>
                    <TextField
                        label="Reply"
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        disabled={sending}
                    />
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!replyText.trim() || sending}
                        >
                            {sending ? <CircularProgress size={24} /> : 'Send'}
                        </Button>
                    </Box>
                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                </ReplyForm>
            </Box> */}
        </Box>

    );
};

export default MessageDetail;